import React, { useEffect, useState } from 'react';
import { Form, Select, Button, Input, Alert, Tag } from 'antd';
import { SearchOutlined, FolderOpenOutlined, UserAddOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import axiosInstance from '../../variables/api/axiosInstance';
import VictimeModal from '../../components/VictimeModal';
import CBModal from '../../components/CBModal';
import { useTranslation } from 'react-i18next';

const { Option } = Select;
const { TextArea } = Input;

const GREEN = '#1a6b3c';

/* Corps de la victime créée depuis l'ouverture d'un dossier : tout ce que l'agent saisit
   dans VictimeModal. Situation, type de dommage, profession, part de responsabilité,
   adresse électronique et téléphone y manquaient : la victime naissait « neutre »
   alors que l'agent l'avait déclarée blessée ou décédée. */
export const corpsVictimeReclamation = (vals, sinistreTrackingId) => ({
  nom:                     vals.nom?.trim(),
  prenoms:                 vals.prenoms?.trim(),
  dateNaissance:           vals.dateNaissance || null,
  sexe:                    vals.sexe,
  nationalite:             vals.nationalite || null,
  langue:                  vals.langue || null,
  telephone:               vals.telephone || null,
  email:                   vals.email || null,
  statutActivite:          vals.statutActivite || null,
  revenuMensuel:           vals.revenuMensuel != null ? Number(vals.revenuMensuel) : null,
  paysResidenceTrackingId: vals.paysResidenceTrackingId || null,
  sinistreTrackingId,
  typeVictime:             vals.typeVictime || null,
  typeDommage:             vals.typeDommage || null,
  estAdversaire:           !!vals.estAdversaire,
  profession:              vals.profession || null,
  tauxRc:                  vals.tauxRc != null ? Number(vals.tauxRc) : null,
});

export default function NouveauDossierModal({ open, onClose, onCreated, defaultSinistreId = null, defaultVictimeId = null }) {
  const { t } = useTranslation('reclamations');
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const [searchSinistre, setSearchSinistre] = useState('');
  const [sinistresOptions, setSinistresOptions] = useState([]);
  const [sinistreChoisi, setSinistreChoisi] = useState(null);

  const [victimes, setVictimes] = useState([]);
  const [loadingVictimes, setLoadingVictimes] = useState(false);

  const [paysList, setPaysList] = useState([]);
  const [victimeModalOpen, setVictimeModalOpen] = useState(false);
  const [savingVictime, setSavingVictime] = useState(false);

  /* Pré-sélection du sinistre si fourni (auto-ouverture depuis le détail d'un sinistre) */
  useEffect(() => {
    if (!open || !defaultSinistreId) return;
    axiosInstance
      .get(`/v1/sinistres/${defaultSinistreId}`)
      .then(r => {
        const s = r.data?.data;
        if (!s) return;
        setSinistresOptions([s]);
        setSinistreChoisi(s);
        form.setFieldValue('sinistreTrackingId', s.sinistreTrackingId);
      })
      .catch(() => {});
  }, [open, defaultSinistreId, form]);

  /* Recherche sinistre */
  useEffect(() => {
    if (!open || !searchSinistre || searchSinistre.length < 2) {
      setSinistresOptions([]);
      return;
    }
    const t = setTimeout(() => {
      axiosInstance
        .get(`/v1/sinistres?search=${encodeURIComponent(searchSinistre)}&size=10`)
        .then(r => {
          const d = r.data?.data;
          setSinistresOptions(Array.isArray(d) ? d : (d?.content || []));
        })
        .catch(() => setSinistresOptions([]));
    }, 300);
    return () => clearTimeout(t);
  }, [searchSinistre, open]);

  /* Chargement des victimes du sinistre choisi */
  const reloadVictimes = () => {
    if (!sinistreChoisi?.sinistreTrackingId) return;
    setLoadingVictimes(true);
    axiosInstance
      .get(`/v1/victimes/sinistre/${sinistreChoisi.sinistreTrackingId}`)
      .then(r => setVictimes(Array.isArray(r.data?.data) ? r.data.data : []))
      .catch(() => setVictimes([]))
      .finally(() => setLoadingVictimes(false));
  };

  useEffect(() => {
    if (!sinistreChoisi?.sinistreTrackingId) {
      setVictimes([]);
      return;
    }
    reloadVictimes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sinistreChoisi]);

  /* Ouverture pour une personne précise (« Ouvrir son dossier » depuis la liste) */
  useEffect(() => {
    if (open && defaultVictimeId && victimes.some(v => v.victimeTrackingId === defaultVictimeId)) {
      form.setFieldValue('victimeTrackingId', defaultVictimeId);
    }
  }, [open, defaultVictimeId, victimes, form]);

  /* Chargement de la liste des pays (pour le champ paysResidence dans VictimeModal) */
  useEffect(() => {
    if (!open) return;
    axiosInstance.get('/v1/pays')
      .then(r => setPaysList(Array.isArray(r.data?.data) ? r.data.data : []))
      .catch(() => setPaysList([]));
  }, [open]);

  /* Création d'une victime inline (on POST /v1/victimes puis on recharge la liste) */
  const handleSaveVictime = async (vals) => {
    if (!sinistreChoisi?.sinistreTrackingId) return;
    setSavingVictime(true);
    try {
      const res = await axiosInstance.post('/v1/victimes', corpsVictimeReclamation(vals, sinistreChoisi.sinistreTrackingId));
      const created = res.data?.data;
      toast.success(t('nouveauDossier.victimeAjoutee'));
      setVictimeModalOpen(false);
      reloadVictimes();
      if (created?.victimeTrackingId) {
        form.setFieldValue('victimeTrackingId', created.victimeTrackingId);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || t('nouveauDossier.erreurVictime'));
    } finally {
      setSavingVictime(false);
    }
  };

  /* Reset à l'ouverture (le pré-remplissage sinistre se fait dans le useEffect suivant) */
  useEffect(() => {
    if (open) {
      form.resetFields();
      setSinistreChoisi(null);
      setVictimes([]);
      setSearchSinistre('');
      setSinistresOptions([]);
    }
  }, [open, form]);

  const onFinish = async (vals) => {
    setLoading(true);
    try {
      const payload = {
        sinistreTrackingId: vals.sinistreTrackingId,
        victimeTrackingId:  vals.victimeTrackingId,
        notesRedacteur:     vals.notesRedacteur?.trim() || null,
      };
      const res = await axiosInstance.post('/v1/dossiers-reclamation', payload);
      const dossier = res.data?.data;
      toast.success(dossier?.numeroDossier ? t('nouveauDossier.ouvert', { numero: dossier.numeroDossier }) : t('nouveauDossier.ouvertSansNumero'));
      onCreated?.(dossier);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.message || t('nouveauDossier.erreurDossier'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <CBModal
      title={
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>
            <FolderOpenOutlined style={{ color: GREEN, marginRight: 8 }} />
            {t('nouveauDossier.titre')}
          </div>
          <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 400, marginTop: 2 }}>
            {t('nouveauDossier.sousTitre')}
          </div>
        </div>
      }
      open={open} onCancel={onClose} footer={null} width={640} destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Form.Item
          name="sinistreTrackingId"
          label={t('nouveauDossier.sinistreConcerne')}
          rules={[{ required: true, message: t('nouveauDossier.sinistreRequis') }]}
        >
          <Select
            showSearch
            allowClear
            disabled={Boolean(defaultSinistreId)}
            placeholder={t('nouveauDossier.rechercheSinistre')}
            suffixIcon={<SearchOutlined />}
            filterOption={false}
            onSearch={setSearchSinistre}
            onChange={(v) => {
              const s = sinistresOptions.find(o => o.sinistreTrackingId === v);
              setSinistreChoisi(s || null);
              form.setFieldValue('victimeTrackingId', undefined);
            }}
            notFoundContent={
              searchSinistre.length < 2 ? t('nouveauDossier.saisirDeuxCaracteres') : t('nouveauDossier.aucunResultat')
            }
            options={sinistresOptions.map(s => ({
              label: (
                <span>
                  <strong style={{ color: GREEN }}>{s.numeroSinistreLocal}</strong>
                  <span style={{ color: '#6b7280', marginLeft: 8, fontSize: 12 }}>
                    · {s.assureNomComplet || '-'} · {s.typeDommage}
                  </span>
                </span>
              ),
              value: s.sinistreTrackingId,
            }))}
          />
        </Form.Item>

        {sinistreChoisi && !loadingVictimes && victimes.length === 0 && (
          <Alert
            showIcon
            type="warning"
            style={{ borderRadius: 10, marginBottom: 16 }}
            message={t('nouveauDossier.aucuneVictime')}
            description={
              <>
                <div style={{ marginBottom: 8 }}>
                  {t('nouveauDossier.aucuneVictimeAide')}
                </div>
                <Button
                  size="small"
                  type="primary"
                  icon={<UserAddOutlined />}
                  onClick={() => setVictimeModalOpen(true)}
                  style={{ background: GREEN, borderColor: GREEN }}
                >
                  {t('nouveauDossier.ajouterVictime')}
                </Button>
              </>
            }
          />
        )}

        <Form.Item
          name="victimeTrackingId"
          label={t('commun.victime')}
          rules={[{ required: true, message: t('nouveauDossier.victimeRequise') }]}
          extra={
            !sinistreChoisi
              ? t('nouveauDossier.choisirSinistreDabord')
              : loadingVictimes
                ? t('nouveauDossier.chargementVictimes')
                : null
          }
        >
          <Select
            placeholder={victimes.length === 0 ? t('nouveauDossier.aucuneVictimeDisponible') : t('nouveauDossier.selectionnerVictime')}
            disabled={!sinistreChoisi || loadingVictimes || victimes.length === 0}
          >
            {victimes.map(v => (
              <Option key={v.victimeTrackingId} value={v.victimeTrackingId}>
                <span style={{ fontWeight: 500 }}>{v.nom} {v.prenoms}</span>
                <Tag
                  color={v.typeVictime === 'DECEDE' ? 'red' : v.typeVictime === 'BLESSE' ? 'orange' : 'default'}
                  style={{ borderRadius: 20, fontSize: 11, marginLeft: 8 }}
                >
                  {v.typeVictime}
                </Tag>
              </Option>
            ))}
          </Select>
        </Form.Item>

        {sinistreChoisi && (
          <Alert
            showIcon
            type="info"
            style={{ borderRadius: 10, marginBottom: 16 }}
            message={
              <span>
                {t('nouveauDossier.sinistre')} <strong>{sinistreChoisi.numeroSinistreLocal}</strong>
                {' '}· {sinistreChoisi.typeDommage}
                {' '}· {sinistreChoisi.typeSinistre === 'SURVENU_TOGO' ? t('nouveauDossier.survenuTogo') : t('nouveauDossier.survenuEtranger')}
              </span>
            }
          />
        )}

        <Form.Item name="notesRedacteur" label={t('nouveauDossier.notes')}>
          <TextArea rows={3} placeholder={t('nouveauDossier.notesPlaceholder')} maxLength={1000} showCount />
        </Form.Item>

        <div style={{
          borderTop: '1px solid #e5e7eb', paddingTop: 16, marginTop: 8,
          display: 'flex', justifyContent: 'flex-end', gap: 8,
        }}>
          <Button onClick={onClose} disabled={loading}>{t('commun.annuler')}</Button>
          <Button
            type="primary" htmlType="submit" loading={loading}
            disabled={sinistreChoisi && victimes.length === 0}
            style={{ background: GREEN, borderColor: GREEN, minWidth: 140 }}
          >
            {t('nouveauDossier.ouvrir')}
          </Button>
        </div>
      </Form>

      {/* Modal inline d'ajout rapide d'une victime sur le sinistre courant */}
      <VictimeModal
        open={victimeModalOpen}
        onClose={() => setVictimeModalOpen(false)}
        onSave={handleSaveVictime}
        loading={savingVictime}
        pays={paysList}
      />
    </CBModal>
  );
}
